/** Avatar color palette shared by client + server. */
export const AVATAR_COLORS = [
  "#FF6B6B",
  "#4ECDC4",
  "#45B7D1",
  "#96CEB4",
  "#FFEAA7",
  "#DDA0DD",
  "#98D8C8",
  "#F7DC6F",
  "#BB8FCE",
  "#85C1E9",
  "#F8C471",
  "#82E0AA",
] as const;

export const LIMITS = {
  MAX_USERNAME_LENGTH: 20,
  MAX_MESSAGE_LENGTH: 200,
  /** Server: max chat messages per sliding window. */
  RATE_LIMIT_MAX: 8,
  /** Server: sliding window duration in ms. */
  RATE_LIMIT_WINDOW_MS: 10_000,
  /** Client: minimum ms between sends (UX throttle). */
  CLIENT_RATE_LIMIT_MS: 2_000,
  /** Max chat messages kept in the DOM. */
  MAX_CHAT_MESSAGES: 200,
  /** Theater seats per row. */
  THEATER_COLS: 8,
  /** Server: how long a user stays "in the room" after their socket drops. */
  PRESENCE_GRACE_MS: 20_000,
  /** Server: recent chat messages persisted & replayed to (re)joiners. */
  CHAT_HISTORY_SIZE: 50,
  /** Max flashcards per room deck. */
  MAX_FLASHCARDS: 40,
  MAX_FLASH_TERM: 120,
  MAX_FLASH_DEFINITION: 500,
  /** Private room code length. */
  ROOM_CODE_LENGTH: 6,
  /** Max length of a room password (join overlay + /lock). */
  MAX_ROOM_PASSWORD: 64,
  /** Polls auto-close after this long (slash system, Phase 1.3). */
  POLL_TTL_MS: 30 * 60_000,
  /** Max poll options. */
  POLL_MAX_OPTIONS: 8,
  /**
   * Max media_sync messages per window per session (playhead telemetry;
   * excess dropped silently — last-writer-wins, next sync re-anchors).
   */
  MEDIA_SYNC_MAX: 30,
  /**
   * Max chat+whisper messages per window per IP — bounds the D1 write +
   * broadcast amplification of session-minting scripts (~5x a single user,
   * so a shared couch of 4-5 people never notices).
   */
  CHAT_IP_MAX: 40,
  /** Guest moderation votes (Phase 1.7): how long a vote stays open. */
  VOTE_TTL_MS: 90_000,
  /** Closed votes keep showing results this long before the panel clears. */
  VOTE_RESULTS_KEEP_MS: 2 * 60_000,
  /**
   * Voters (and vote starters) must have been in the room this long — the
   * anti-sock-puppet gate (identities are free to mint; presence age is the
   * only cheap signal). Tests override via the VOTE_MIN_AGE_MS env var.
   */
  VOTE_MIN_AGE_MS: 2 * 60_000,
  /** At least this many total votes for a result to count (quorum). */
  VOTE_QUORUM_MIN: 2,
} as const;

/**
 * Exact WS heartbeat strings for Durable Object `setWebSocketAutoResponse`.
 * Must match byte-for-byte on client send / server pair — do not add fields.
 * Auto-response answers without waking the DO (no duration / handler cost).
 */
export const WS_HEARTBEAT = {
  /**
   * NAT/edge keepalive — answered by the DO runtime's
   * `setWebSocketAutoResponse` WITHOUT waking the Durable Object (cheap).
   */
  PING: '{"type":"ping"}',
  PONG: '{"type":"pong"}',
  /**
   * DO-visible liveness heartbeat — distinct from PING so it DOES wake the
   * DO. Sent every 60s by the client; the server stamps the socket's
   * last-seen and reaps sockets silent past the idle window. This is what
   * makes dead tabs (no close frame, edge never notices) disappear.
   */
  ALIVE: '{"type":"alive"}',
} as const;

export const DEFAULT_ROOM = "global";

/** Private room that paints every chat/moderation UI state for visual QA.
 * Open `?room=dev-showcase` — no second tab needed. */
export const SHOWCASE_ROOM = "dev-showcase";

/** Previous slug — still treated as the playground so old links keep working. */
export const SHOWCASE_ROOM_LEGACY = "showcase";

export const STREAM = {
  HLS_URL: "https://ireplay.tv/test/blender.m3u8",
  HLS_FALLBACK: "https://test-streams.mux.dev/x36xhzz/x36xhzz.m3u8",
} as const;

/** Empty deck on first open — the stage shows a typing-guide placeholder. */
export const DEFAULT_FLASHCARDS: Array<{
  id: string;
  term: string;
  definition: string;
}> = [];

/** Normalize a room slug from URL / user input. */
export function normalizeRoomName(raw: string | null | undefined): string {
  const cleaned = (raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, "")
    .slice(0, 32);
  return cleaned || DEFAULT_ROOM;
}

/** Extract an 11-char YouTube video id from a URL or bare id. */
export function parseYouTubeId(input: string): string | null {
  const s = input.trim();
  if (!s) return null;
  if (/^[\w-]{11}$/.test(s)) return s;

  const patterns = [
    /[?&]v=([\w-]{11})/,
    /youtu\.be\/([\w-]{11})/,
    /youtube\.com\/embed\/([\w-]{11})/,
    /youtube\.com\/shorts\/([\w-]{11})/,
    /youtube\.com\/live\/([\w-]{11})/,
  ];
  for (const re of patterns) {
    const m = s.match(re);
    if (m?.[1]) return m[1];
  }
  return null;
}

/** Generate a short private-room code. */
export function generateRoomCode(length = LIMITS.ROOM_CODE_LENGTH): string {
  const alphabet = "abcdefghijklmnopqrstuvwxyz0123456789";
  const bytes = new Uint8Array(length);
  const c = (globalThis as { crypto?: { getRandomValues(a: Uint8Array): Uint8Array } })
    .crypto;
  if (c?.getRandomValues) {
    c.getRandomValues(bytes);
  } else {
    for (let i = 0; i < length; i++) bytes[i] = (Math.random() * 256) | 0;
  }
  let out = "";
  for (let i = 0; i < length; i++) {
    out += alphabet[bytes[i]! % alphabet.length]!;
  }
  return out;
}

/**
 * Parse pasted flashcard text.
 * Format (one card per line): `Term :: Definition`
 * Blank lines and `#` comments are ignored.
 */
export function parseFlashcardText(raw: string): Array<{ term: string; definition: string }> {
  const out: Array<{ term: string; definition: string }> = [];
  for (const line of raw.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("#")) continue;
    const sep = trimmed.indexOf("::");
    if (sep <= 0) continue;
    const term = trimmed.slice(0, sep).trim().slice(0, LIMITS.MAX_FLASH_TERM);
    const definition = trimmed
      .slice(sep + 2)
      .trim()
      .slice(0, LIMITS.MAX_FLASH_DEFINITION);
    if (term && definition) out.push({ term, definition });
  }
  return out;
}

/** Prompt users can copy to ask an AI for cards in our format. */
export const FLASHCARD_AI_PROMPT = `Convert the following study material into Yay flashcards.

Output ONLY lines in this exact format (no numbering, no bullets, no extra commentary):
Term :: Definition

Rules:
- One flashcard per line
- Use " :: " (space-colon-colon-space) between term and definition
- Keep terms short; keep definitions clear and concise
- Skip anything that isn't a useful study card

Study material:
`;
